import { getElementChild, getElementChildren } from '../Utility/Dom'
import { IS_ACTIVE, IS_HIDDEN } from '../Utility/State'

export type FilterChangeCallback = (state: FilterState) => void

export interface FilterState {
  categories: number[]
  sorting: string
  subcategories: number[]
}

export class FilterBar {
  private readonly container: HTMLElement
  private readonly dropdowns: [] | NodeListOf<HTMLElement>
  private readonly flyout: HTMLElement | null
  private readonly onFilterChange: FilterChangeCallback
  private readonly sortDropdown: HTMLElement | null

  constructor(container: HTMLElement, onFilterChange: FilterChangeCallback) {
    this.container = container
    this.onFilterChange = onFilterChange
    this.dropdowns = getElementChildren(this.container, 'filter-dropdown')
    this.flyout = getElementChild(this.container, 'filter-flyout')
    this.sortDropdown = getElementChild(this.container, 'filter-sort-dropdown')
  }

  public init(): void {
    this.addDropdownEvents()
    this.addFlyoutEvents()
    this.addSortEvents()
  }

  private addDropdownEvents(): void {
    if (this.dropdowns.length === 0) {
      return
    }

    this.dropdowns.forEach((dropdown: HTMLElement): void => {
      const checkboxes = getElementChildren(dropdown, 'filter-checkbox')
      const resetButton = getElementChild(dropdown, 'filter-reset')
      const submitButton = getElementChild(dropdown, 'filter-submit')
      const trigger = getElementChild(dropdown, 'filter-trigger')
      const type = trigger?.dataset.type

      trigger?.addEventListener('click', (): void => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true'
        this.closeAllDropdowns()

        if (!isExpanded) {
          trigger.setAttribute('aria-expanded', 'true')
        }
      })

      checkboxes.forEach((checkbox: HTMLElement): void => {
        checkbox.addEventListener('change', (): void => {
          this.updateResetVisibility(dropdown)
        })
      })

      submitButton?.addEventListener('click', (): void => {
        if (type === 'category') {
          this.resetDropdownByType('subcategory')
          this.filterSubcategoryOptions()
        }

        this.updateTriggerState(dropdown)
        this.closeAllDropdowns()
        this.emitFilterChange()
      })

      resetButton?.addEventListener('click', (): void => {
        checkboxes.forEach((checkbox: HTMLElement): void => {
          (checkbox as HTMLInputElement).checked = false
        })

        this.updateResetVisibility(dropdown)

        if (type === 'category') {
          this.resetDropdownByType('subcategory')
          this.filterSubcategoryOptions()
        }

        this.updateTriggerState(dropdown)
        this.closeAllDropdowns()
        this.emitFilterChange()
      })
    })

    document.addEventListener('click', (event: MouseEvent): void => {
      const target = event.target as HTMLElement

      if (target.closest('[data-js="filter-dropdown"]') === null && target.closest('[data-js="filter-sort-dropdown"]') === null) {
        this.closeAllDropdowns()
      }
    })
  }

  private addFlyoutEvents(): void {
    if (this.flyout === null) {
      return
    }

    const backdrop = this.container.querySelector('[data-js="filter-flyout-backdrop"]') as HTMLElement | null
    const checkboxes = getElementChildren(this.flyout, 'filter-flyout-checkbox')
    const closeButton = getElementChild(this.flyout, 'filter-flyout-close')
    const resetButton = getElementChild(this.flyout, 'filter-flyout-reset')
    const submitButton = getElementChild(this.flyout, 'filter-flyout-submit')
    const trigger = getElementChild(this.container, 'filter-flyout-trigger')

    trigger?.addEventListener('click', (): void => this.openFlyout())
    closeButton?.addEventListener('click', (): void => this.closeFlyout())
    backdrop?.addEventListener('click', (): void => this.closeFlyout())

    checkboxes.forEach((checkbox: HTMLElement): void => {
      checkbox.addEventListener('change', (): void => {
        this.updateFlyoutResetVisibility()

        if ((checkbox as HTMLInputElement).dataset.type === 'category') {
          this.filterFlyoutSubcategoryOptions()
        }
      })
    })

    submitButton?.addEventListener('click', (): void => {
      this.syncDesktopFromFlyout()
      this.updateFlyoutTriggerState()
      this.closeFlyout()
      this.emitFilterChange()
    })

    resetButton?.addEventListener('click', (): void => {
      checkboxes.forEach((checkbox: HTMLElement): void => {
        (checkbox as HTMLInputElement).checked = false
      })

      this.syncDesktopFromFlyout()
      this.updateFlyoutResetVisibility()
      this.updateFlyoutTriggerState()
      this.closeFlyout()
      this.emitFilterChange()
    })
  }

  private addSortEvents(): void {
    if (this.sortDropdown === null) {
      return
    }

    const radios = getElementChildren(this.sortDropdown, 'filter-sort-radio')
    const resetButton = getElementChild(this.sortDropdown, 'filter-sort-reset')
    const submitButton = getElementChild(this.sortDropdown, 'filter-sort-submit')
    const trigger = getElementChild(this.sortDropdown, 'filter-sort-trigger')
    const triggerText = getElementChild(this.sortDropdown, 'filter-sort-trigger-text')

    trigger?.addEventListener('click', (): void => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true'
      this.closeAllDropdowns()

      if (!isExpanded) {
        trigger.setAttribute('aria-expanded', 'true')
      }
    })

    radios.forEach((radio: HTMLElement): void => {
      radio.addEventListener('change', (): void => {
        this.updateSortResetVisibility()
      })
    })

    submitButton?.addEventListener('click', (): void => {
      const selected = this.sortDropdown?.querySelector('[data-js="filter-sort-radio"]:checked') as HTMLInputElement | null

      if (triggerText !== null && selected?.parentElement !== null) {
        triggerText.textContent = selected?.parentElement?.textContent ?? ''
      }

      this.updateSortTriggerState()
      this.closeAllDropdowns()
      this.emitFilterChange()
    })

    resetButton?.addEventListener('click', (): void => {
      const defaultRadio = this.sortDropdown?.querySelector('[data-js="filter-sort-radio"][value="date_desc"]') as HTMLInputElement | null

      if (defaultRadio !== null) {
        defaultRadio.checked = true
      }

      if (triggerText !== null) {
        triggerText.textContent = triggerText.dataset.defaultText ?? triggerText.textContent
      }

      this.updateSortResetVisibility()
      this.updateSortTriggerState()
      this.closeAllDropdowns()
      this.emitFilterChange()
    })
  }

  private closeAllDropdowns(): void {
    this.dropdowns.forEach((dropdown: HTMLElement): void => {
      const trigger = getElementChild(dropdown, 'filter-trigger')
      trigger?.setAttribute('aria-expanded', 'false')
    })

    const sortTrigger = getElementChild(this.sortDropdown, 'filter-sort-trigger')
    sortTrigger?.setAttribute('aria-expanded', 'false')
  }

  private closeFlyout(): void {
    this.flyout?.classList.remove(IS_ACTIVE)
    document.body.style.overflow = ''
  }

  private emitFilterChange(): void {
    this.onFilterChange(this.getState())
  }

  private filterFlyoutSubcategoryOptions(): void {
    if (this.flyout === null) {
      return
    }

    const selectedCategories = this.getValuesFromCheckboxes(
      this.flyout.querySelectorAll('[data-js="filter-flyout-checkbox"][data-type="category"]')
    )

    this.flyout.querySelectorAll('[data-js="filter-flyout-checkbox"][data-type="subcategory"]').forEach((el: Element): void => {
      const input = el as HTMLInputElement
      const option = input.closest('.filter-bar__option')
      const parentValue = input.dataset.parent

      if (parentValue === undefined || option === null) {
        return
      }

      if (selectedCategories.length === 0 || selectedCategories.includes(parseInt(parentValue, 10))) {
        option.classList.remove(IS_HIDDEN)
      } else {
        option.classList.add(IS_HIDDEN)
        input.checked = false
      }
    })
  }

  private filterSubcategoryOptions(): void {
    const subcategoryDropdown = Array.from(this.dropdowns).find((d: HTMLElement): boolean => {
      const trigger = getElementChild(d, 'filter-trigger')
      return trigger?.dataset.type === 'subcategory'
    })

    if (subcategoryDropdown === undefined) {
      return
    }

    const categories = this.getDropdownValues('category')

    subcategoryDropdown.querySelectorAll('.filter-bar__option').forEach((option: Element): void => {
      const checkbox = option.querySelector('[data-js="filter-checkbox"]') as HTMLInputElement | null
      const parentValue = checkbox?.dataset.parent

      if (parentValue === undefined) {
        option.classList.remove(IS_HIDDEN)
        return
      }

      if (categories.length === 0 || categories.includes(parseInt(parentValue, 10))) {
        option.classList.remove(IS_HIDDEN)
      } else {
        option.classList.add(IS_HIDDEN)

        if (checkbox !== null) {
          checkbox.checked = false
        }
      }
    })
  }

  private getDropdownValues(type: string): number[] {
    const dropdown = Array.from(this.dropdowns).find((d: HTMLElement): boolean => {
      const trigger = getElementChild(d, 'filter-trigger')
      return trigger?.dataset.type === type
    })

    if (dropdown === undefined) {
      return []
    }

    return this.getValuesFromCheckboxes(dropdown.querySelectorAll('[data-js="filter-checkbox"]'))
  }

  private getSelectedSorting(): string {
    const radio = this.sortDropdown?.querySelector('[data-js="filter-sort-radio"]:checked') as HTMLInputElement | null
    return radio?.value ?? 'date_desc'
  }

  private getState(): FilterState {
    return {
      categories: this.getDropdownValues('category'),
      sorting: this.getSelectedSorting(),
      subcategories: this.getDropdownValues('subcategory'),
    }
  }

  private getValuesFromCheckboxes(checkboxes: NodeListOf<Element>): number[] {
    const values: number[] = []

    checkboxes.forEach((el: Element): void => {
      const input = el as HTMLInputElement

      if (input.checked && input.dataset.value) {
        values.push(parseInt(input.dataset.value, 10))
      }
    })

    return values
  }

  private openFlyout(): void {
    this.flyout?.classList.add(IS_ACTIVE)
    document.body.style.overflow = 'hidden'
  }

  private resetDropdownByType(type: string): void {
    const dropdown = Array.from(this.dropdowns).find((d: HTMLElement): boolean => {
      const trigger = getElementChild(d, 'filter-trigger')
      return trigger?.dataset.type === type
    })

    if (dropdown === undefined) {
      return
    }

    getElementChildren(dropdown, 'filter-checkbox').forEach((checkbox: HTMLElement): void => {
      (checkbox as HTMLInputElement).checked = false
    })

    this.updateResetVisibility(dropdown)
    this.updateTriggerState(dropdown)
  }

  private syncDesktopFromFlyout(): void {
    if (this.flyout === null) {
      return
    }

    const flyoutCheckboxes = this.flyout.querySelectorAll('[data-js="filter-flyout-checkbox"]')
    const selectedValues = new Map<string, Set<string>>()

    flyoutCheckboxes.forEach((el: Element): void => {
      const input = el as HTMLInputElement
      const type = input.dataset.type ?? ''
      const value = input.dataset.value ?? ''

      if (!selectedValues.has(type)) {
        selectedValues.set(type, new Set())
      }

      if (input.checked) {
        selectedValues.get(type)?.add(value)
      }
    })

    this.dropdowns.forEach((dropdown: HTMLElement): void => {
      const trigger = getElementChild(dropdown, 'filter-trigger')
      const type = trigger?.dataset.type ?? ''
      const selected = selectedValues.get(type) ?? new Set()

      getElementChildren(dropdown, 'filter-checkbox').forEach((checkbox: HTMLElement): void => {
        const input = checkbox as HTMLInputElement
        input.checked = selected.has(input.dataset.value ?? '')
      })

      this.updateResetVisibility(dropdown)
      this.updateTriggerState(dropdown)
    })

  }

  private updateFlyoutResetVisibility(): void {
    if (this.flyout === null) {
      return
    }

    const checkboxes = this.flyout.querySelectorAll('[data-js="filter-flyout-checkbox"]')
    const resetButton = getElementChild(this.flyout, 'filter-flyout-reset')
    const hasSelection = Array.from(checkboxes).some((el: Element): boolean => (el as HTMLInputElement).checked)

    if (hasSelection) {
      resetButton?.classList.remove(IS_HIDDEN)
    } else {
      resetButton?.classList.add(IS_HIDDEN)
    }
  }

  private updateFlyoutTriggerState(): void {
    const trigger = getElementChild(this.container, 'filter-flyout-trigger')
    const count = getElementChild(this.container, 'filter-flyout-count')

    if (trigger === null || this.flyout === null) {
      return
    }

    const checkboxes = this.flyout.querySelectorAll('[data-js="filter-flyout-checkbox"]')
    const selectedCount = Array.from(checkboxes).filter((el: Element): boolean => (el as HTMLInputElement).checked).length

    if (selectedCount > 0) {
      trigger.classList.add('has-selection')
      count?.classList.remove(IS_HIDDEN)

      if (count !== null) {
        count.textContent = selectedCount.toString()
      }
    } else {
      trigger.classList.remove('has-selection')
      count?.classList.add(IS_HIDDEN)
    }
  }

  private updateResetVisibility(dropdown: HTMLElement): void {
    const checkboxes = getElementChildren(dropdown, 'filter-checkbox')
    const resetButton = getElementChild(dropdown, 'filter-reset')
    const hasSelection = Array.from(checkboxes).some((cb: HTMLElement): boolean => (cb as HTMLInputElement).checked)

    if (hasSelection) {
      resetButton?.classList.remove(IS_HIDDEN)
    } else {
      resetButton?.classList.add(IS_HIDDEN)
    }
  }

  private updateSortResetVisibility(): void {
    if (this.sortDropdown === null) {
      return
    }

    const resetButton = getElementChild(this.sortDropdown, 'filter-sort-reset')
    const selected = this.sortDropdown.querySelector('[data-js="filter-sort-radio"]:checked') as HTMLInputElement | null
    const isDefault = selected?.value === 'date_desc'

    if (isDefault) {
      resetButton?.classList.add(IS_HIDDEN)
    } else {
      resetButton?.classList.remove(IS_HIDDEN)
    }
  }

  private updateSortTriggerState(): void {
    if (this.sortDropdown === null) {
      return
    }

    const trigger = getElementChild(this.sortDropdown, 'filter-sort-trigger')
    const selected = this.sortDropdown.querySelector('[data-js="filter-sort-radio"]:checked') as HTMLInputElement | null
    const isDefault = selected?.value === 'date_desc'

    if (isDefault) {
      trigger?.classList.remove('has-selection')
    } else {
      trigger?.classList.add('has-selection')
    }
  }

  private updateTriggerState(dropdown: HTMLElement): void {
    const checkboxes = getElementChildren(dropdown, 'filter-checkbox')
    const count = getElementChild(dropdown, 'filter-count')
    const trigger = getElementChild(dropdown, 'filter-trigger')
    const selectedCount = Array.from(checkboxes).filter((cb: HTMLElement): boolean => (cb as HTMLInputElement).checked).length

    if (selectedCount > 0) {
      trigger?.classList.add('has-selection')
      count?.classList.remove(IS_HIDDEN)

      if (count !== null) {
        count.textContent = selectedCount.toString()
      }
    } else {
      trigger?.classList.remove('has-selection')
      count?.classList.add(IS_HIDDEN)
    }
  }
}
